import {
  Sparkles, ShoppingBag, LineChart, Megaphone, Code2, Smartphone, Palette, Workflow
} from "lucide-react";

export const services = [
  {
    slug: "ai",
    icon: Sparkles,
    name_en: "AI Solutions",
    name_ar: "حلول الذكاء الاصطناعي",
    short_en: "Chatbots, automation, predictive analytics and AI assistants that compound ROI.",
    short_ar: "روبوتات محادثة وأتمتة وتحليلات تنبؤية ومساعدات ذكاء اصطناعي تضاعف العائد.",
    accent: "cyan",
    size: "lg",
    features_en: [
      "Conversational AI & chatbots",
      "Workflow automation",
      "Predictive analytics",
      "Recommendation systems",
      "AI-powered customer support",
      "Custom LLM integrations",
      "Data-driven decision support",
      "Intelligent document processing",
    ],
    features_ar: [
      "الذكاء الاصطناعي الحواري والروبوتات",
      "أتمتة سير العمل",
      "التحليلات التنبؤية",
      "أنظمة التوصية",
      "دعم عملاء مدعوم بالذكاء الاصطناعي",
      "تكاملات نماذج لغوية مخصصة",
      "دعم القرار القائم على البيانات",
      "معالجة ذكية للمستندات",
    ],
    description_en: [
      "Most \"AI\" pilots in the Gulf never leave the pilot stage — they impress in a demo and die in production. We build differently: production-grade AI systems wired into your real data, your real workflows and your compliance requirements from day one.",
      "Our engineers pair LLM orchestration with classical machine learning where it's the better tool — recommendation engines, forecasting models, document classifiers — so you get accuracy and cost efficiency, not just a chatbot wrapper. Every system ships with monitoring, fallback logic and human-in-the-loop controls for anything customer-facing or regulated.",
      "We've shipped Arabic-first conversational AI, agentic back-office automation, and predictive analytics for retail, finance and government clients — and we stay on as the team that tunes, retrains and extends what we built.",
    ],
    description_ar: [
      "معظم تجارب الذكاء الاصطناعي في الخليج لا تتجاوز مرحلة التجربة — تبهر في العرض التقديمي ثم تتعثر في الإنتاج. نحن نبني بشكل مختلف: أنظمة ذكاء اصطناعي جاهزة للإنتاج، متصلة ببياناتكم الحقيقية وسير عملكم الفعلي ومتطلبات الامتثال منذ اليوم الأول.",
      "يجمع مهندسونا بين تنسيق النماذج اللغوية الكبيرة والتعلم الآلي التقليدي حين يكون الخيار الأنسب — محركات توصية، نماذج تنبؤية، مصنفات مستندات — لتحصلوا على دقة وكفاءة تكلفة، لا مجرد واجهة محادثة. كل نظام يُسلَّم مع مراقبة وآليات احتياطية وإشراف بشري على أي جزء يتعامل مع العملاء أو خاضع لتنظيم.",
      "نفّذنا ذكاءً اصطناعياً محادثياً يراعي العربية أولاً، وأتمتة وكيلية للعمليات الخلفية، وتحليلات تنبؤية لعملاء في التجزئة والتمويل والقطاع الحكومي — ونبقى كفريق يواصل ضبط وإعادة تدريب وتوسيع ما بنيناه.",
    ],
    deliverables_en: [
      "Production LLM integration with monitoring & fallback logic",
      "Custom-trained models for classification, forecasting or recommendations",
      "Agentic workflow automation across your existing tools",
      "Arabic and English conversational AI with proper dialect handling",
      "Data pipeline & vector store architecture",
      "Model evaluation harness and ongoing retraining plan",
    ],
    deliverables_ar: [
      "تكامل نماذج لغوية جاهز للإنتاج مع مراقبة وآليات احتياطية",
      "نماذج مدرّبة خصيصاً للتصنيف أو التنبؤ أو التوصية",
      "أتمتة وكيلية لسير العمل عبر أدواتكم الحالية",
      "ذكاء اصطناعي محادثي بالعربية والإنجليزية مع مراعاة اللهجات",
      "بنية خطوط بيانات ومخازن متجهات",
      "إطار تقييم نماذج وخطة إعادة تدريب مستمرة",
    ],
    engagement_en: "Discovery sprint (1-2 weeks) to validate the use case against your data, then a fixed-scope build (6-10 weeks) or an embedded pod for ongoing AI roadmap work.",
    engagement_ar: "مرحلة اكتشاف (1-2 أسبوع) للتحقق من الحالة مقابل بياناتكم، ثم تنفيذ محدد النطاق (6-10 أسابيع) أو فريق مدمج لخارطة طريق ذكاء اصطناعي مستمرة.",
    howWeWork_en: [
      { step: "01", title: "Audit your data", desc: "We assess data quality, volume and access before promising any model." },
      { step: "02", title: "Prototype fast", desc: "A working proof-of-concept against real data within the first two weeks." },
      { step: "03", title: "Harden for production", desc: "Monitoring, guardrails, fallback paths and load testing before go-live." },
      { step: "04", title: "Tune and extend", desc: "We stay on to retrain models and expand the system as your data grows." },
    ],
    howWeWork_ar: [
      { step: "01", title: "تدقيق بياناتكم", desc: "نقيّم جودة البيانات وحجمها وإمكانية الوصول إليها قبل أي وعد بنموذج." },
      { step: "02", title: "نموذج أولي سريع", desc: "إثبات مفهوم يعمل على بياناتكم الحقيقية خلال أول أسبوعين." },
      { step: "03", title: "تجهيز للإنتاج", desc: "مراقبة وضوابط احتياطية واختبار تحمل قبل الإطلاق." },
      { step: "04", title: "ضبط وتوسيع", desc: "نبقى لإعادة تدريب النماذج وتوسيع النظام مع نمو بياناتكم." },
    ],
    useCase_en: {
      problem: "A retail group's support team was drowning in repetitive Arabic and English tickets, with average response time over 6 hours.",
      solution: "We shipped a bilingual AI assistant trained on their product catalog and policies, with human escalation for edge cases.",
      impact: "Response time dropped to under 4 minutes; 61% of tickets now resolve without a human agent.",
    },
    useCase_ar: {
      problem: "فريق دعم مجموعة تجزئة كان يغرق في تذاكر متكررة بالعربية والإنجليزية، بمتوسط زمن استجابة يتجاوز 6 ساعات.",
      solution: "نفّذنا مساعداً ذكياً ثنائي اللغة مدرَّباً على كتالوج المنتجات والسياسات، مع تصعيد بشري للحالات الاستثنائية.",
      impact: "انخفض زمن الاستجابة إلى أقل من 4 دقائق؛ و61% من التذاكر تُحل الآن دون تدخل بشري.",
    },
  },
  {
    slug: "ecommerce",
    icon: ShoppingBag,
    name_en: "E-Commerce Development",
    name_ar: "تطوير التجارة الإلكترونية",
    short_en: "Online stores, marketplaces and subscription commerce built to convert.",
    short_ar: "متاجر إلكترونية وأسواق وتجارة اشتراكات تصمم للتحويل.",
    accent: "blue",
    size: "md",
    features_en: [
      "Custom store development",
      "Marketplace platforms",
      "Payment gateway integration",
      "Inventory & order management",
      "Subscription commerce",
      "CRO & analytics",
      "Headless commerce",
      "Customer journey optimization",
    ],
    features_ar: [
      "تطوير متاجر مخصصة",
      "منصات الأسواق",
      "تكامل بوابات الدفع",
      "إدارة المخزون والطلبات",
      "تجارة الاشتراكات",
      "تحسين التحويل والتحليلات",
      "التجارة المنفصلة",
      "تحسين رحلة العميل",
    ],
    description_en: [
      "A beautiful storefront that doesn't convert is a cost center, not an asset. We build commerce platforms around one measure: revenue per visitor — from checkout friction to payment gateway reliability to post-purchase retention.",
      "Whether you need a headless Shopify build, a custom Next.js storefront, or a multi-vendor marketplace, we architect for the traffic spikes of Ramadan and White Friday, not just steady-state load. Local payment rails — mada, STC Pay, Tabby, Tamara — and Arabic-first checkout are standard, not an afterthought.",
      "Every build ships with CRO instrumentation from day one — funnel analytics, A/B testing infrastructure and heatmaps — so growth work can start the week you launch, not six months later.",
    ],
    description_ar: [
      "متجر جميل لا يحقق تحويلاً هو مركز تكلفة لا أصل. نبني منصات تجارة إلكترونية حول مقياس واحد: الإيراد لكل زائر — من احتكاك الدفع إلى موثوقية بوابة الدفع إلى الاحتفاظ بعد الشراء.",
      "سواء احتجتم متجر Shopify منفصلاً أو واجهة Next.js مخصصة أو سوقاً متعدد البائعين، نصمم البنية لتحمل ذروة حركة رمضان والجمعة البيضاء، لا حمل الحالة الثابتة فقط. قنوات الدفع المحلية — مدى و STC Pay وتابي وتمارا — والدفع بالعربية أولاً معيار قياسي لا إضافة لاحقة.",
      "كل تنفيذ يُسلَّم مع أدوات قياس تحسين التحويل منذ اليوم الأول — تحليلات القمع وبنية اختبار A/B وخرائط حرارية — لتبدأ أعمال النمو أسبوع الإطلاق لا بعد ستة أشهر.",
    ],
    deliverables_en: [
      "Custom or headless storefront (Shopify, Next.js, or platform-agnostic)",
      "Local payment gateway integration (mada, STC Pay, Tabby, Tamara, Apple Pay)",
      "Inventory, order and fulfillment management",
      "Bilingual, RTL-correct checkout and product pages",
      "CRO instrumentation: funnels, A/B testing, heatmaps",
      "Load-tested for peak traffic (Ramadan, White Friday, campaign launches)",
    ],
    deliverables_ar: [
      "متجر مخصص أو منفصل (Shopify أو Next.js أو مستقل عن المنصة)",
      "تكامل بوابات دفع محلية (مدى، STC Pay، تابي، تمارا، Apple Pay)",
      "إدارة المخزون والطلبات والتوصيل",
      "صفحات دفع ومنتجات ثنائية اللغة وصحيحة الاتجاه",
      "أدوات تحسين التحويل: قمع المبيعات، اختبار A/B، خرائط حرارية",
      "اختبار تحمل لحركة الذروة (رمضان، الجمعة البيضاء، إطلاق الحملات)",
    ],
    engagement_en: "Scoping workshop (3-5 days) to lock information architecture and integrations, then an 8-14 week build depending on catalog complexity and custom features.",
    engagement_ar: "ورشة تحديد نطاق (3-5 أيام) لضبط بنية المعلومات والتكاملات، ثم تنفيذ يمتد 8-14 أسبوعاً حسب تعقيد الكتالوج والميزات المخصصة.",
    howWeWork_en: [
      { step: "01", title: "Map the funnel", desc: "We trace your actual buying journey and find where revenue leaks today." },
      { step: "02", title: "Design to convert", desc: "Product, cart and checkout flows built around proven CRO patterns." },
      { step: "03", title: "Build & integrate", desc: "Storefront, payments, inventory and fulfillment wired end to end." },
      { step: "04", title: "Launch & optimize", desc: "Instrumented from day one so growth work starts immediately post-launch." },
    ],
    howWeWork_ar: [
      { step: "01", title: "رسم القمع", desc: "نتتبع رحلة الشراء الفعلية ونحدد أين يتسرب الإيراد اليوم." },
      { step: "02", title: "تصميم للتحويل", desc: "تدفقات المنتج والسلة والدفع مبنية وفق أنماط مثبتة لتحسين التحويل." },
      { step: "03", title: "بناء وتكامل", desc: "المتجر والدفع والمخزون والتوصيل موصولة من طرف لطرف." },
      { step: "04", title: "إطلاق وتحسين", desc: "مُجهَّز بالقياس منذ اليوم الأول لتبدأ أعمال النمو فور الإطلاق." },
    ],
    useCase_en: {
      problem: "A fashion retailer's mobile checkout abandoned 74% of carts, with no visibility into where users dropped off.",
      solution: "We rebuilt checkout as a single-page flow with saved payment methods, Tabby BNPL and real-time stock validation.",
      impact: "Cart abandonment fell to 51%; mobile conversion rate rose 2.1x within the first full sales cycle.",
    },
    useCase_ar: {
      problem: "دفع الجوال لدى متجر أزياء كان يفقد 74% من السلات دون أي رؤية لمكان تسرّب المستخدمين.",
      solution: "أعدنا بناء الدفع كتدفق بصفحة واحدة مع طرق دفع محفوظة وتابي للدفع الآجل وتحقق مخزون لحظي.",
      impact: "انخفض هجر السلة إلى 51%؛ وارتفع معدل تحويل الجوال 2.1 ضعفاً خلال أول دورة مبيعات كاملة.",
    },
  },
  {
    slug: "consultancy",
    icon: LineChart,
    name_en: "Business Consultancy",
    name_ar: "الاستشارات التجارية",
    short_en: "Strategy, market entry, operations and growth planning for ambitious teams.",
    short_ar: "الاستراتيجية ودخول السوق والعمليات وتخطيط النمو للفرق الطموحة.",
    accent: "cyan",
    size: "md",
    features_en: [
      "Business strategy",
      "Market entry & expansion",
      "Operational optimization",
      "Revenue model design",
      "KPI frameworks",
      "Organizational structuring",
      "Digital transformation roadmaps",
      "Growth planning",
    ],
    features_ar: [
      "استراتيجية الأعمال",
      "دخول السوق والتوسع",
      "تحسين العمليات",
      "تصميم نموذج الإيرادات",
      "أطر مؤشرات الأداء",
      "الهيكلة التنظيمية",
      "خارطة التحول الرقمي",
      "تخطيط النمو",
    ],
    description_en: [
      "Strategy decks that sit in a drawer are worthless. Our consultancy work always ends in a build-ready plan — the KPIs, the org changes, the sequencing — handed to a team, often ours, that can execute it the same quarter.",
      "We work across market entry, operating model redesign and digital transformation roadmaps for Gulf-based and regional-expansion clients, combining regulatory and cultural fluency with the same technical depth our engineering teams bring to implementation.",
      "Because we build software ourselves, our recommendations are grounded in what's actually shippable in 90 days versus what needs 18 months — not theoretical frameworks disconnected from delivery reality.",
    ],
    description_ar: [
      "العروض الاستراتيجية التي تبقى في الأدراج بلا قيمة. عملنا الاستشاري ينتهي دائماً بخطة جاهزة للتنفيذ — المؤشرات، التغييرات التنظيمية، التسلسل — تُسلَّم لفريق، غالباً فريقنا، قادر على تنفيذها في الربع ذاته.",
      "نعمل عبر دخول الأسواق وإعادة تصميم النموذج التشغيلي وخرائط التحول الرقمي لعملاء في الخليج وعملاء يوسّعون إقليمياً، جامعين بين الإلمام التنظيمي والثقافي والعمق التقني ذاته الذي تحمله فرق هندستنا للتنفيذ.",
      "ولأننا نبني البرمجيات بأنفسنا، توصياتنا مبنية على ما يمكن تسليمه فعلاً خلال 90 يوماً مقابل ما يحتاج 18 شهراً — لا أطراً نظرية منفصلة عن واقع التنفيذ.",
    ],
    deliverables_en: [
      "Market entry and expansion strategy",
      "Operating model and org structure redesign",
      "Revenue model and pricing architecture",
      "KPI frameworks with dashboards, not just slide decks",
      "Digital transformation roadmap sequenced by quarter",
      "Vendor and build-vs-buy technology assessments",
    ],
    deliverables_ar: [
      "استراتيجية دخول السوق والتوسع",
      "إعادة تصميم النموذج التشغيلي والهيكل التنظيمي",
      "نموذج الإيرادات وبنية التسعير",
      "أطر مؤشرات أداء مع لوحات متابعة، لا عروضاً تقديمية فقط",
      "خارطة تحول رقمي مجزأة على أرباع السنة",
      "تقييم الموردين وقرارات البناء مقابل الشراء التقني",
    ],
    engagement_en: "Diagnostic phase (2-3 weeks) followed by a roadmap sprint; ongoing advisory retainers available for boards and leadership teams.",
    engagement_ar: "مرحلة تشخيص (2-3 أسابيع) تليها ورشة خارطة طريق؛ وعقود استشارية مستمرة متاحة لمجالس الإدارة وفرق القيادة.",
    howWeWork_en: [
      { step: "01", title: "Diagnose", desc: "Structured interviews, data review and market analysis — no assumptions." },
      { step: "02", title: "Frame the bets", desc: "A short list of high-conviction moves, not a 60-slide deck of options." },
      { step: "03", title: "Sequence the roadmap", desc: "Quarter-by-quarter plan with owners, budgets and measurable outcomes." },
      { step: "04", title: "Support execution", desc: "We stay engaged through delivery — ours or your internal team's." },
    ],
    howWeWork_ar: [
      { step: "01", title: "التشخيص", desc: "مقابلات منظمة ومراجعة بيانات وتحليل سوق — بلا افتراضات مسبقة." },
      { step: "02", title: "تأطير الرهانات", desc: "قائمة قصيرة من التحركات عالية الثقة، لا عرضاً بستين شريحة من الخيارات." },
      { step: "03", title: "تسلسل الخارطة", desc: "خطة ربعية بمالكين وميزانيات ونتائج قابلة للقياس." },
      { step: "04", title: "دعم التنفيذ", desc: "نبقى منخرطين خلال التنفيذ — سواء بفريقنا أو فريقكم الداخلي." },
    ],
    useCase_en: {
      problem: "A family-owned distributor wanted to expand from the UAE into Saudi Arabia but had no clear operating model for a second market.",
      solution: "We built a phased market-entry plan covering licensing, local partnership structure, and a lean regional org chart.",
      impact: "Saudi operations launched within 5 months of the roadmap being approved, ahead of the original 9-month internal estimate.",
    },
    useCase_ar: {
      problem: "موزّع عائلي أراد التوسع من الإمارات إلى السعودية دون نموذج تشغيلي واضح لسوق ثانٍ.",
      solution: "بنينا خطة دخول سوق مرحلية تغطي الترخيص وهيكل الشراكة المحلية وهيكلاً تنظيمياً إقليمياً مرناً.",
      impact: "انطلقت العمليات السعودية خلال 5 أشهر من اعتماد الخارطة، أسرع من التقدير الداخلي الأصلي البالغ 9 أشهر.",
    },
  },
  {
    slug: "digital-marketing",
    icon: Megaphone,
    name_en: "Digital Marketing",
    name_ar: "التسويق الرقمي",
    short_en: "SEO, paid media, content and lead generation — with full-funnel reporting.",
    short_ar: "تحسين البحث، الإعلانات، المحتوى وتوليد العملاء مع تقارير شاملة.",
    accent: "blue",
    size: "lg",
    features_en: [
      "SEO & content marketing",
      "Google Ads & Meta Ads",
      "Social media management",
      "Email marketing",
      "Influencer campaigns",
      "Analytics & attribution",
      "Lead generation",
      "Performance reporting",
    ],
    features_ar: [
      "تحسين محركات البحث والمحتوى",
      "إعلانات جوجل وميتا",
      "إدارة وسائل التواصل",
      "التسويق عبر البريد",
      "حملات المؤثرين",
      "التحليلات والإسناد",
      "توليد العملاء",
      "تقارير الأداء",
    ],
    description_en: [
      "Most agencies report on activity — impressions, reach, engagement. We report on pipeline and revenue, because that's what actually gets budgets renewed. Every campaign is built with attribution wired in before the first dirham is spent.",
      "We run performance media across Google, Meta, TikTok and Snap, SEO and content, and lifecycle email/WhatsApp — across English and Arabic audiences with genuinely localized creative, not machine-translated copy.",
      "Our team includes former in-house growth leads from Gulf e-commerce and fintech brands, so campaign strategy is grounded in what actually moves CAC and LTV in this market, not generic playbooks.",
    ],
    description_ar: [
      "معظم الوكالات تقدّم تقارير عن النشاط — الظهور والوصول والتفاعل. نحن نقدّم تقارير عن خط المبيعات والإيراد، لأن هذا ما يُجدَّد الميزانيات فعلاً. كل حملة تُبنى بإسناد مُفعّل قبل إنفاق أول درهم.",
      "ندير الإعلانات المدفوعة عبر جوجل وميتا و TikTok و Snap، وتحسين البحث والمحتوى، والتسويق الدوري عبر البريد وواتساب — لجمهور عربي وإنجليزي بمحتوى محلي حقيقي، لا ترجمة آلية.",
      "يضم فريقنا قادة نمو سابقين من علامات تجارة إلكترونية وتقنية مالية خليجية، فاستراتيجية الحملات مبنية على ما يحرّك فعلاً تكلفة الاكتساب والقيمة الدائمة في هذا السوق، لا أدلة عامة.",
    ],
    deliverables_en: [
      "Full-funnel paid media (Google, Meta, TikTok, Snap)",
      "SEO and content strategy with Arabic and English editorial",
      "Lifecycle email and WhatsApp marketing",
      "Multi-touch attribution and dashboarding",
      "Creative production: ad copy, video, landing pages",
      "Monthly performance reporting tied to revenue, not vanity metrics",
    ],
    deliverables_ar: [
      "إعلانات مدفوعة شاملة للقمع (جوجل، ميتا، TikTok، Snap)",
      "استراتيجية تحسين بحث ومحتوى بالعربية والإنجليزية",
      "تسويق دوري عبر البريد وواتساب",
      "إسناد متعدد اللمسات ولوحات متابعة",
      "إنتاج إبداعي: نصوص إعلانية، فيديو، صفحات هبوط",
      "تقارير أداء شهرية مرتبطة بالإيراد لا بمؤشرات شكلية",
    ],
    engagement_en: "Audit and strategy sprint (2 weeks), then a monthly retainer with quarterly strategy resets — typically a 6-month minimum to let attribution data mature.",
    engagement_ar: "مراجعة وورشة استراتيجية (أسبوعان)، ثم عقد شهري مع إعادة ضبط استراتيجي ربعي — عادةً بحد أدنى 6 أشهر لنضج بيانات الإسناد.",
    howWeWork_en: [
      { step: "01", title: "Audit", desc: "Full teardown of current channels, creative and attribution setup." },
      { step: "02", title: "Build the engine", desc: "Tracking, audiences and creative systems set up before scaling spend." },
      { step: "03", title: "Launch & test", desc: "Structured A/B testing across creative, audience and offer." },
      { step: "04", title: "Scale what works", desc: "Budget shifts weekly toward the channels and creative proven to convert." },
    ],
    howWeWork_ar: [
      { step: "01", title: "المراجعة", desc: "تفكيك كامل للقنوات الحالية والإبداعيات وإعداد الإسناد." },
      { step: "02", title: "بناء المحرك", desc: "إعداد التتبع والجمهور والأنظمة الإبداعية قبل توسيع الإنفاق." },
      { step: "03", title: "الإطلاق والاختبار", desc: "اختبار A/B منظم عبر الإبداعيات والجمهور والعرض." },
      { step: "04", title: "توسيع ما ينجح", desc: "تحويل الميزانية أسبوعياً نحو القنوات والإبداعيات المثبتة التحويل." },
    ],
    useCase_en: {
      problem: "A Gulf fintech was spending heavily on paid social but couldn't tell which channel drove actual signups, only clicks.",
      solution: "We rebuilt their attribution stack with server-side events and incrementality testing across Meta, Google and TikTok.",
      impact: "Qualified lead volume grew 5.4x while customer acquisition cost dropped 31% over two quarters.",
    },
    useCase_ar: {
      problem: "شركة تقنية مالية خليجية كانت تنفق بكثافة على الإعلانات الاجتماعية دون معرفة أي قناة تحرّك التسجيلات الفعلية، بل النقرات فقط.",
      solution: "أعدنا بناء منظومة الإسناد لديها بأحداث من جانب الخادم واختبارات تزايدية عبر ميتا وجوجل و TikTok.",
      impact: "نما حجم العملاء المؤهلين 5.4 أضعاف بينما انخفضت تكلفة الاكتساب 31% خلال ربعين.",
    },
  },
  {
    slug: "web-development",
    icon: Code2,
    name_en: "Website Development",
    name_ar: "تطوير المواقع",
    short_en: "Marketing sites, web apps and portals — performant, secure and scalable.",
    short_ar: "مواقع تسويقية وتطبيقات وبوابات — سريعة وآمنة وقابلة للتوسع.",
    accent: "cyan",
    size: "md",
    features_en: [
      "React / Next.js applications",
      "Headless CMS integration",
      "Performance & Core Web Vitals tuning",
      "Secure, scalable APIs",
      "Bilingual EN/AR with correct RTL",
      "Accessibility (WCAG 2.2 AA) built in",
    ],
    features_ar: [
      "تطبيقات React / Next.js",
      "تكامل أنظمة إدارة محتوى منفصلة",
      "تحسين الأداء ومؤشرات الويب الأساسية",
      "واجهات API آمنة وقابلة للتوسع",
      "ثنائية اللغة مع دعم صحيح للاتجاه من اليمين لليسار",
      "إتاحة وصول متوافقة مع WCAG 2.2 AA",
    ],
    description_en: [
      "Your website is often the first — and sometimes only — interaction a government evaluator, investor or enterprise buyer has with your company before a meeting. We build sites that hold up to that scrutiny: fast, secure and structurally sound, not just visually polished.",
      "We work in React and Next.js by default, with headless CMS architecture so your team can update content without filing a ticket. Every build ships with Core Web Vitals budgets enforced in CI, proper Arabic RTL typography — not mirrored English — and accessibility as a requirement, not an audit finding.",
      "For portals with real complexity — multi-role dashboards, document workflows, integrations with legacy systems — we bring the same engineering rigor as our AI and automation work, because most \"marketing sites\" quietly become the operational system of record within a year.",
    ],
    description_ar: [
      "موقعكم غالباً أول — وأحياناً الوحيد — تفاعل يخوضه مقيّم حكومي أو مستثمر أو مشترٍ مؤسسي مع شركتكم قبل أي اجتماع. نبني مواقع تصمد أمام هذا التدقيق: سريعة وآمنة وسليمة البنية، لا مصقولة بصرياً فقط.",
      "نعمل بـ React و Next.js افتراضياً، مع بنية إدارة محتوى منفصلة ليتولى فريقكم تحديث المحتوى دون تذكرة دعم. كل تنفيذ يُسلَّم مع موازنات أداء مفروضة في خط التكامل المستمر، وطباعة عربية صحيحة الاتجاه — لا إنجليزية معكوسة — وإتاحة وصول كمتطلب لا كملاحظة تدقيق لاحقة.",
      "للبوابات ذات التعقيد الحقيقي — لوحات متعددة الأدوار، سير عمل مستندي، تكامل مع أنظمة قديمة — نجلب صرامة هندسية مماثلة لعملنا في الذكاء الاصطناعي والأتمتة، لأن معظم \"المواقع التسويقية\" تتحول بصمت إلى نظام التشغيل الفعلي خلال عام.",
    ],
    deliverables_en: [
      "Marketing site or web application in React/Next.js",
      "Headless CMS so your team owns content updates",
      "Performance budget enforced in CI (Core Web Vitals)",
      "Bilingual EN/AR with correct RTL, not mirrored layout",
      "WCAG 2.2 AA accessibility",
      "Hosting, monitoring and security hardening",
    ],
    deliverables_ar: [
      "موقع تسويقي أو تطبيق ويب بـ React/Next.js",
      "نظام إدارة محتوى منفصل يملكه فريقكم بالكامل",
      "موازنة أداء مفروضة في خط التكامل المستمر",
      "ثنائي اللغة مع اتجاه صحيح، لا تخطيطاً معكوساً",
      "إتاحة وصول متوافقة مع WCAG 2.2 AA",
      "استضافة ومراقبة وتحصين أمني",
    ],
    engagement_en: "Scoping (3-5 days), then a 4-10 week build depending on page count, integrations and CMS complexity.",
    engagement_ar: "تحديد نطاق (3-5 أيام)، ثم تنفيذ يمتد 4-10 أسابيع حسب عدد الصفحات والتكاملات وتعقيد نظام المحتوى.",
    howWeWork_en: [
      { step: "01", title: "Scope & architect", desc: "Information architecture, content model and integrations mapped upfront." },
      { step: "02", title: "Design system first", desc: "Reusable components before individual pages, so the site scales cleanly." },
      { step: "03", title: "Build with budgets", desc: "Performance and accessibility checked in CI on every commit." },
      { step: "04", title: "Launch & harden", desc: "Security review, monitoring and a 30-day post-launch support window." },
    ],
    howWeWork_ar: [
      { step: "01", title: "التحديد والتصميم", desc: "بنية المعلومات ونموذج المحتوى والتكاملات مرسومة مسبقاً." },
      { step: "02", title: "نظام التصميم أولاً", desc: "مكونات قابلة لإعادة الاستخدام قبل الصفحات الفردية، لتوسع نظيف." },
      { step: "03", title: "بناء بموازنات", desc: "فحص الأداء والإتاحة في خط التكامل المستمر مع كل تعديل." },
      { step: "04", title: "إطلاق وتحصين", desc: "مراجعة أمنية ومراقبة ونافذة دعم 30 يوماً بعد الإطلاق." },
    ],
    useCase_en: {
      problem: "A professional services firm's old WordPress site took 8+ seconds to load on mobile and had no Arabic version.",
      solution: "We rebuilt on Next.js with a headless CMS and true bilingual routing, launching a properly localized Arabic experience.",
      impact: "Mobile load time dropped to under 1.8 seconds; organic search traffic grew 3.2x within four months.",
    },
    useCase_ar: {
      problem: "موقع WordPress قديم لشركة خدمات مهنية كان يستغرق أكثر من 8 ثوانٍ للتحميل على الجوال ودون نسخة عربية.",
      solution: "أعدنا البناء بـ Next.js مع نظام محتوى منفصل وتوجيه ثنائي اللغة حقيقي، وأطلقنا تجربة عربية محلية فعلياً.",
      impact: "انخفض زمن التحميل على الجوال إلى أقل من 1.8 ثانية؛ ونمت حركة البحث العضوي 3.2 ضعفاً خلال أربعة أشهر.",
    },
  },
  {
    slug: "mobile-apps",
    icon: Smartphone,
    name_en: "Mobile Apps",
    name_ar: "تطبيقات الهاتف",
    short_en: "iOS, Android and cross-platform apps engineered for real user impact.",
    short_ar: "تطبيقات iOS وأندرويد ومتعددة المنصات لتأثير حقيقي على المستخدم.",
    accent: "blue",
    size: "md",
    features_en: [
      "iOS & Android native or cross-platform (React Native)",
      "Offline-first architecture",
      "Push notifications & deep linking",
      "App Store & Google Play launch management",
      "Biometric auth & secure storage",
      "Analytics and crash reporting built in",
    ],
    features_ar: [
      "iOS وأندرويد أصلي أو متعدد المنصات (React Native)",
      "بنية تعمل دون اتصال أولاً",
      "إشعارات فورية وروابط عميقة",
      "إدارة الإطلاق على App Store و Google Play",
      "مصادقة بيومترية وتخزين آمن",
      "تحليلات وتتبع أعطال مدمجة",
    ],
    description_en: [
      "Mobile is where Gulf consumers actually transact — mobile commerce and mobile banking adoption here outpaces most Western markets. An app that lags, crashes on older Android devices, or breaks on a bad connection loses trust fast, and it doesn't come back.",
      "We build native (Swift/Kotlin) when performance or platform depth demands it, and React Native when shared codebase speed matters more — deciding based on your actual requirements, not a default preference. Offline-first architecture and resilient sync are standard for any app that can't assume a stable connection.",
      "We manage the full path to launch, including App Store and Google Play review requirements, and we stay on for the first release cycles when crash rates and adoption data actually reveal what needs fixing.",
    ],
    description_ar: [
      "الجوال هو حيث يتعامل مستهلكو الخليج فعلاً — تبني التجارة والخدمات المصرفية عبر الجوال هنا يتجاوز معظم الأسواق الغربية. تطبيق بطيء أو يتعطل على أجهزة أندرويد قديمة أو ينهار مع اتصال ضعيف يفقد الثقة بسرعة، ولا تعود.",
      "نبني بشكل أصلي (Swift/Kotlin) حين يتطلب الأداء أو عمق المنصة ذلك، وبـ React Native حين تكون سرعة قاعدة الشيفرة المشتركة الأهم — نقرر بناءً على متطلباتكم الفعلية لا تفضيل افتراضي. البنية التي تعمل دون اتصال أولاً والمزامنة المرنة معيار قياسي لأي تطبيق لا يمكنه افتراض اتصال مستقر.",
      "ندير المسار الكامل حتى الإطلاق، بما في ذلك متطلبات مراجعة App Store و Google Play، ونبقى خلال أول دورات إصدار حين تكشف بيانات الأعطال والتبني فعلياً ما يحتاج إصلاحاً.",
    ],
    deliverables_en: [
      "Native (Swift/Kotlin) or cross-platform (React Native) app",
      "Offline-first data sync architecture",
      "Push notifications, deep linking, biometric auth",
      "App Store & Google Play submission and launch management",
      "Analytics and crash reporting instrumentation",
      "Post-launch support through the first release cycles",
    ],
    deliverables_ar: [
      "تطبيق أصلي (Swift/Kotlin) أو متعدد المنصات (React Native)",
      "بنية مزامنة بيانات تعمل دون اتصال أولاً",
      "إشعارات فورية وروابط عميقة ومصادقة بيومترية",
      "تقديم وإدارة إطلاق على App Store و Google Play",
      "تحليلات وتتبع أعطال مدمجة",
      "دعم ما بعد الإطلاق خلال أول دورات الإصدار",
    ],
    engagement_en: "Discovery and UX flow mapping (1-2 weeks), then an 8-16 week build depending on scope and platform count.",
    engagement_ar: "اكتشاف ورسم تدفقات تجربة المستخدم (1-2 أسبوع)، ثم تنفيذ يمتد 8-16 أسبوعاً حسب النطاق وعدد المنصات.",
    howWeWork_en: [
      { step: "01", title: "Map the flows", desc: "Every screen and edge case designed before a line of app code is written." },
      { step: "02", title: "Build the core", desc: "Core flows and data sync built and tested first — the risky parts, early." },
      { step: "03", title: "Polish & harden", desc: "Performance on older devices, offline states, and store review checklist." },
      { step: "04", title: "Launch & iterate", desc: "We monitor crash rates and adoption data through the first release cycles." },
    ],
    howWeWork_ar: [
      { step: "01", title: "رسم التدفقات", desc: "كل شاشة وحالة استثنائية مصممة قبل كتابة أي سطر شيفرة." },
      { step: "02", title: "بناء النواة", desc: "التدفقات الأساسية والمزامنة تُبنى وتُختبر أولاً — الأجزاء الأكثر خطورة، مبكراً." },
      { step: "03", title: "الصقل والتحصين", desc: "الأداء على الأجهزة القديمة، الحالات دون اتصال، وقائمة مراجعة المتجر." },
      { step: "04", title: "الإطلاق والتكرار", desc: "نراقب معدلات الأعطال وبيانات التبني خلال أول دورات الإصدار." },
    ],
    useCase_en: {
      problem: "A logistics operator's driver app frequently lost location and delivery data on poor-connectivity routes outside the city.",
      solution: "We rebuilt the app with offline-first sync, queuing updates locally and reconciling once connectivity returned.",
      impact: "Data loss incidents dropped to near zero; driver-reported app crashes fell 89%.",
    },
    useCase_ar: {
      problem: "تطبيق سائقي شركة لوجستية كان يفقد بيانات الموقع والتسليم بشكل متكرر على طرق ضعيفة التغطية خارج المدينة.",
      solution: "أعدنا بناء التطبيق بمزامنة تعمل دون اتصال أولاً، تُخزّن التحديثات محلياً وتُوفّق عند عودة الاتصال.",
      impact: "انخفضت حوادث فقدان البيانات إلى شبه صفر؛ وتراجعت أعطال التطبيق المبلَّغ عنها من السائقين 89%.",
    },
  },
  {
    slug: "branding",
    icon: Palette,
    name_en: "Branding & Creative",
    name_ar: "العلامة التجارية والإبداع",
    short_en: "Identity, messaging and creative systems that make your brand unforgettable.",
    short_ar: "هوية ورسائل وأنظمة إبداعية تجعل علامتك لا تُنسى.",
    accent: "cyan",
    size: "md",
    features_en: [
      "Brand identity & visual systems",
      "Messaging & positioning frameworks",
      "Naming & verbal identity",
      "Design systems for product & marketing",
      "Launch creative: pitch decks, social kits, ad templates",
      "Bilingual brand voice (English & Arabic)",
    ],
    features_ar: [
      "الهوية البصرية وأنظمة العلامة",
      "أطر الرسائل والتموضع",
      "التسمية والهوية اللفظية",
      "أنظمة تصميم للمنتج والتسويق",
      "إبداعيات الإطلاق: عروض تقديمية وحزم تواصل اجتماعي وقوالب إعلانات",
      "صوت العلامة ثنائي اللغة (الإنجليزية والعربية)",
    ],
    description_en: [
      "A brand that only exists in English isn't a Gulf brand — it's a translated one. We build identity systems where the Arabic wordmark, typography and voice are designed natively, not adapted after the English version ships.",
      "Beyond the logo, we define the messaging architecture and design system your team actually uses day-to-day: templates, components and voice guidelines that keep every deck, ad and product screen consistent without a designer in every meeting.",
      "For clients building product alongside brand, we hand off directly into the same design system our engineering teams implement — so the brand doesn't erode the moment it touches code.",
    ],
    description_ar: [
      "علامة تجارية موجودة بالإنجليزية فقط ليست علامة خليجية — بل مترجمة. نبني أنظمة هوية يُصمَّم فيها الشعار الكتابي العربي والطباعة والصوت بشكل أصيل، لا يُكيَّف بعد إطلاق النسخة الإنجليزية.",
      "إلى جانب الشعار، نحدد بنية الرسائل ونظام التصميم الذي يستخدمه فريقكم فعلياً يومياً: قوالب ومكونات وإرشادات صوت تحافظ على اتساق كل عرض وإعلان وشاشة منتج دون حاجة لمصمم في كل اجتماع.",
      "للعملاء الذين يبنون منتجاً مع العلامة معاً، نُسلّم مباشرة في نظام التصميم ذاته الذي تنفذه فرق هندستنا — فلا تتآكل العلامة لحظة ملامستها للشيفرة.",
    ],
    deliverables_en: [
      "Full identity system: logo, color, typography, iconography",
      "Bilingual visual and verbal identity (native Arabic, not translated)",
      "Messaging architecture and positioning framework",
      "Design system components for product and marketing",
      "Launch asset kit: decks, social templates, ad formats",
      "Brand guidelines documentation",
    ],
    deliverables_ar: [
      "نظام هوية كامل: شعار، ألوان، طباعة، أيقونات",
      "هوية بصرية ولفظية ثنائية اللغة (عربية أصيلة لا مترجمة)",
      "بنية رسائل وإطار تموضع",
      "مكونات نظام تصميم للمنتج والتسويق",
      "حزمة أصول إطلاق: عروض، قوالب تواصل اجتماعي، صيغ إعلانية",
      "توثيق إرشادات العلامة",
    ],
    engagement_en: "Discovery and positioning workshop (1-2 weeks), then a 4-8 week identity build depending on system depth.",
    engagement_ar: "ورشة اكتشاف وتموضع (1-2 أسبوع)، ثم بناء هوية يمتد 4-8 أسابيع حسب عمق النظام.",
    howWeWork_en: [
      { step: "01", title: "Discover", desc: "Positioning workshop with stakeholders — what the brand must stand for." },
      { step: "02", title: "Explore territory", desc: "Concept directions tested in both English and Arabic simultaneously." },
      { step: "03", title: "Build the system", desc: "Full identity system and reusable design components, not just a logo." },
      { step: "04", title: "Launch-ready handoff", desc: "Guidelines and asset kits your team can actually use without us." },
    ],
    howWeWork_ar: [
      { step: "01", title: "الاكتشاف", desc: "ورشة تموضع مع أصحاب المصلحة — ما الذي يجب أن تمثله العلامة." },
      { step: "02", title: "استكشاف الاتجاهات", desc: "توجهات مفاهيمية تُختبر بالعربية والإنجليزية في آن واحد." },
      { step: "03", title: "بناء النظام", desc: "نظام هوية كامل ومكونات تصميم قابلة لإعادة الاستخدام، لا شعاراً فقط." },
      { step: "04", title: "تسليم جاهز للإطلاق", desc: "إرشادات وحزم أصول يستخدمها فريقكم فعلياً دوننا." },
    ],
    useCase_en: {
      problem: "A hospitality startup's brand looked premium in English marketing but the Arabic version was a mirrored, machine-translated afterthought.",
      solution: "We designed a native Arabic wordmark and typography system alongside the English identity, built from the same brand strategy.",
      impact: "The rebrand shipped across web, app and print in both languages simultaneously — with zero \"translated\" feel reported in user testing.",
    },
    useCase_ar: {
      problem: "علامة شركة ضيافة ناشئة بدت راقية في التسويق الإنجليزي بينما كانت النسخة العربية معكوسة ومترجمة آلياً كفكرة لاحقة.",
      solution: "صممنا شعاراً كتابياً عربياً أصيلاً ونظام طباعة عربي جنباً إلى جنب مع الهوية الإنجليزية، مبنيَين على الاستراتيجية ذاتها.",
      impact: "أُطلقت العلامة الجديدة عبر الويب والتطبيق والمطبوعات باللغتين معاً — دون أي شعور بـ\"الترجمة\" في اختبارات المستخدمين.",
    },
  },
  {
    slug: "digital-transformation",
    icon: Workflow,
    name_en: "Digital Transformation",
    name_ar: "التحول الرقمي",
    short_en: "Modernize operations end-to-end — from legacy migration to automation.",
    short_ar: "تحديث شامل للعمليات من الترحيل إلى الأتمتة.",
    accent: "blue",
    size: "lg",
    features_en: [
      "Legacy system migration",
      "Process automation",
      "Cloud & infra architecture",
      "Change management",
      "Systems integration",
      "Risk-sequenced delivery roadmaps",
    ],
    features_ar: [
      "ترحيل الأنظمة القديمة",
      "أتمتة العمليات",
      "بنية السحابة والبنية التحتية",
      "إدارة التغيير",
      "تكامل الأنظمة",
      "خرائط طريق تسليم مرتبة حسب المخاطر",
    ],
    description_en: [
      "Digital transformation projects fail for the same reason most of the time: they start with technology and work backward to a business case. We start with the operating friction — the manual handoffs, the legacy system nobody wants to touch, the process that takes six approvals — and work forward to the technology.",
      "That means legacy system migration, process automation and cloud infrastructure work, sequenced around what actually unblocks revenue or reduces risk first, not what's technically interesting first. Change management — training, documentation, adoption tracking — ships alongside the technology, not as an afterthought nobody budgeted for.",
      "We've done this work inside government service delivery, logistics operations and financial back-offices across the Gulf, which means the roadmap accounts for procurement cycles and compliance review, not just ideal-world sprint velocity.",
    ],
    description_ar: [
      "مشاريع التحول الرقمي تفشل غالباً لسبب واحد: تبدأ بالتقنية وتعمل بالعكس نحو مبرر تجاري. نحن نبدأ بالاحتكاك التشغيلي — التسليم اليدوي، النظام القديم الذي لا يريد أحد لمسه، العملية التي تحتاج ست موافقات — ونعمل للأمام نحو التقنية.",
      "هذا يعني ترحيل أنظمة قديمة وأتمتة عمليات وعمل بنية سحابية، مرتبة حسب ما يفتح فعلاً الإيراد أو يقلل المخاطر أولاً، لا ما هو مثير تقنياً أولاً. إدارة التغيير — التدريب والتوثيق وتتبع التبني — تُسلَّم مع التقنية، لا كفكرة لاحقة لم تُرصد لها ميزانية.",
      "نفّذنا هذا العمل داخل تقديم الخدمات الحكومية والعمليات اللوجستية والعمليات الخلفية المالية عبر الخليج، ما يعني أن الخارطة تراعي دورات الشراء ومراجعة الامتثال، لا سرعة تنفيذ مثالية نظرياً فقط.",
    ],
    deliverables_en: [
      "Legacy system migration and modernization",
      "Process automation across manual, paper-based or spreadsheet workflows",
      "Cloud and infrastructure architecture",
      "Change management: training, documentation, adoption tracking",
      "Integration layer connecting legacy and modern systems",
      "Phased roadmap sequenced by risk and revenue impact",
    ],
    deliverables_ar: [
      "ترحيل وتحديث الأنظمة القديمة",
      "أتمتة العمليات اليدوية أو الورقية أو القائمة على جداول البيانات",
      "بنية سحابية وبنية تحتية",
      "إدارة تغيير: تدريب وتوثيق وتتبع تبني",
      "طبقة تكامل تربط الأنظمة القديمة بالحديثة",
      "خارطة طريق مرحلية مرتبة حسب المخاطر وأثر الإيراد",
    ],
    engagement_en: "Operating assessment (2-4 weeks) to map current-state friction, then a phased delivery roadmap — typically 3-12 months depending on legacy system complexity.",
    engagement_ar: "تقييم تشغيلي (2-4 أسابيع) لرسم الاحتكاك الحالي، ثم خارطة تسليم مرحلية — عادةً 3-12 شهراً حسب تعقيد الأنظمة القديمة.",
    howWeWork_en: [
      { step: "01", title: "Assess current state", desc: "We map the real workflow, not the org chart's idealized version of it." },
      { step: "02", title: "Sequence by impact", desc: "Highest-friction, highest-risk processes first — not the easiest wins." },
      { step: "03", title: "Migrate & automate", desc: "Legacy systems modernized in phases, with rollback plans at each stage." },
      { step: "04", title: "Drive adoption", desc: "Training and change management so the new system actually gets used." },
    ],
    howWeWork_ar: [
      { step: "01", title: "تقييم الوضع الحالي", desc: "نرسم سير العمل الفعلي، لا النسخة المثالية في الهيكل التنظيمي." },
      { step: "02", title: "الترتيب حسب الأثر", desc: "العمليات الأعلى احتكاكاً ومخاطرة أولاً — لا الانتصارات الأسهل." },
      { step: "03", title: "الترحيل والأتمتة", desc: "تحديث الأنظمة القديمة على مراحل، مع خطط تراجع في كل مرحلة." },
      { step: "04", title: "دفع التبني", desc: "تدريب وإدارة تغيير ليُستخدم النظام الجديد فعلياً." },
    ],
    useCase_en: {
      problem: "A logistics operator processed customs and shipping documents manually across three ERPs, creating SLA misses and 22,000+ hours of annual manual work.",
      solution: "We built a computer-vision document pipeline and workflow automation layer connecting all three systems.",
      impact: "22,000 hours saved annually; SLA accuracy rose to 99.4%; operations cost fell 54%.",
    },
    useCase_ar: {
      problem: "شركة لوجستية كانت تعالج مستندات الجمارك والشحن يدوياً عبر ثلاثة أنظمة تخطيط موارد، ما تسبب في تجاوز اتفاقيات الخدمة وأكثر من 22,000 ساعة عمل يدوي سنوياً.",
      solution: "بنينا خط معالجة مستندات بالرؤية الحاسوبية وطبقة أتمتة سير عمل تربط الأنظمة الثلاثة.",
      impact: "توفير 22,000 ساعة سنوياً؛ ارتفاع دقة اتفاقية الخدمة إلى 99.4%؛ انخفاض تكلفة العمليات 54%.",
    },
  },
];

export const getService = (slug) => services.find((s) => s.slug === slug);
